import isEqual from 'react-fast-compare';

import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import FormulaUtil, {CellInsertLoc, CellInsertMode} from 'notebook/tables/excess/FormulaUtil';
import {CellColors, TypedValue} from 'notebook/tables/TableConstants';

const NO_CELLS = {};

export interface SpreadsheetStoreState {
  editingTable: string | null;
  formula: string;
  formulaHTML: string;
  typedValue: TypedValue | null;
  cellColors: {[tableName: string]: CellColors};
  selection: {cid: string; start: number; end: number} | null;
  cellInsertLoc: CellInsertLoc | null;
}

export interface SpreadsheetStorePayload extends SpreadsheetStoreState {
  tableNamespace: string;
  tableName: string;
  editing: boolean;
  cellInsertMode: CellInsertMode;
}

/**
 * Manages the formula currently being edited. Allows editing the formula for one table over all open
 * spreadsheet-containing documents (i.e. NoteEditor, StepsEditor)
 */
class SpreadsheetStore extends Store {
  activeNamespace!: string | null;
  activeTableName!: string | null;
  editingTable!: SpreadsheetStoreState['editingTable'];
  formula!: SpreadsheetStoreState['formula'];
  formulaHTML!: SpreadsheetStoreState['formulaHTML'];
  typedValue!: SpreadsheetStoreState['typedValue'];
  cellColors!: SpreadsheetStoreState['cellColors'];
  selection!: SpreadsheetStoreState['selection'];
  cellInsertLoc!: SpreadsheetStoreState['cellInsertLoc'];

  resetState(): void {
    this.activeNamespace = null;
    this.activeTableName = null;
    this.editingTable = null;
    this.formula = '';
    this.formulaHTML = '';
    this.typedValue = null;
    this.cellColors = NO_CELLS;
    this.selection = null;
    this.cellInsertLoc = null;
  }

  /**
   * Returns the current state of the spreadsheet store for the given namespace and table name.
   * If the namespace or tableName are not provided, then it will just return the value for whatever table
   * or namespace are currently active.
   */
  getState(namespace: string | null, tableName?: string): SpreadsheetStoreState {
    // If `namespace` is not active, the document does not have a cell that is being actively edited.
    if (namespace !== this.activeNamespace || (tableName && tableName !== this.activeTableName)) {
      return {
        formula: '',
        formulaHTML: '',
        typedValue: null,
        cellColors: NO_CELLS,
        editingTable: null,
        selection: null,
        cellInsertLoc: null,
      };
    }
    return {
      formula: this.formula,
      formulaHTML: this.formulaHTML,
      typedValue: this.typedValue,
      cellColors: this.cellColors,
      editingTable: this.editingTable,
      selection: this.selection,
      cellInsertLoc: this.cellInsertLoc,
    };
  }

  payloadHandler(action: FluxAction): boolean {
    // Casting from any into known type.
    const payload = action as unknown as SpreadsheetStorePayload;
    switch (action.actionType) {
      case EventConstants.SPREADSHEET_SET_FORMULA:
        this.selection = payload.selection || null;
        this.activeNamespace = payload.tableNamespace;
        this.activeTableName = payload.tableName;
        this.editingTable = payload.editing ? payload.tableName : null;
        this.typedValue = payload.typedValue;
        if (payload.formula != null) {
          const {formulaHTML, cellColors, cellInsertLoc} = FormulaUtil.getFormattedFormula(
            payload.tableName,
            payload.formula,
            // TODO: `this.selection` is only updated when the formula updated, not when the cursor is moved
            // by the user, which means any formula rendering that is a function of selection will be stale,
            // unless the selection is kept up-to-date (i.e. component constantly pushes selection to Store).
            this.selection,
            payload.cellInsertMode
          );
          this.formula = payload.formula;
          this.formulaHTML = formulaHTML;
          if (!isEqual(cellColors, this.cellColors)) {
            this.cellColors = cellColors;
          }
          if (!isEqual(cellInsertLoc, this.cellInsertLoc)) {
            this.cellInsertLoc = cellInsertLoc;
          }
        } else {
          this.resetState();
        }
        this.emitChange();
        break;
      case EventConstants.SPREADSHEET_SET_TYPED_VALUE: {
        this.typedValue = payload.typedValue;
        this.emitChange();
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const spreadsheetStore = new SpreadsheetStore(AppDispatcher);

export default spreadsheetStore;
